import React from 'react';
import { Row, Col, Table, Card } from 'reactstrap';
import CodeBlock from '../CodeBlock';

function AuthenticationSection() {
    return (
        <section id="authentication">
            <Row>
                <Col>
                    <h2>Authentication Server (OAuth2)</h2>
                    <p>
                        What matters to us is keeping candidates &amp; recruiters data secure. Therefore, we implemented
                        an <b>authentication server</b> that is described in more detail below.{' '}
                    </p>
                    <p>
                        Candidate Search API is protected using OAuth2 authorization. In order to access API methods a valid
                        authorization token needs to be included in each request header. The token can be retrieved by requesting
                        OAuth endpoint.
                    </p>
                    <h3>Test endpoint URLs for retrieving access token</h3>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Site</th>
                                <th>Base API URL</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>TotalJobs</td>
                                <td>
                                    <CodeBlock language="json">
                                        https://recruiter.totaljobs.tjgtest.com/authorisationserver/token
                                    </CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>Caterer</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.caterer.tjgtest.com/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>Caterer Global</td>
                                <td>
                                    <CodeBlock CodeBlock>
                                        https://recruiter.catererglobal.tjgtest.com/authorisationserver/token
                                    </CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>CWJobs</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.cwjobs.tjgtest.com/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>RetailChoice</td>
                                <td>
                                    <CodeBlock CodeBlock>
                                        https://recruiter.retailchoice.tjgtest.com/authorisationserver/token
                                    </CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>CareerStructure</td>
                                <td>
                                    <CodeBlock CodeBlock>
                                        https://recruiter.careerstructure.tjgtest.com/authorisationserver/token
                                    </CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>Jobsite</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.jobsite.tjgtest.com/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>Cityjobs</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.cityjobs.tjgtest.com/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>eMedCareers</td>
                                <td>
                                    <CodeBlock CodeBlock>
                                        https://recruiter.emedcareers.tjgtest.com/authorisationserver/token
                                    </CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>Just Engineers</td>
                                <td>
                                    <CodeBlock CodeBlock>
                                        https://recruiter.justengineers.tjgtest.com/authorisationserver/token
                                    </CodeBlock>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <h3>Live endpoint URLs for retrieving access token</h3>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Site</th>
                                <th>Base API URL</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>TotalJobs</td>
                                <td>
                                    <CodeBlock language="json">https://recruiter.totaljobs.com/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>Caterer</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.caterer.com/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>Caterer Global</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.catererglobal.com/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>CWJobs</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.cwjobs.co.uk/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>RetailChoice</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.retailchoice.com/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>CareerStructure</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.careerstructure.com/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>Jobsite</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.jobsite.co.uk/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>Cityjobs</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.cityjobs.com/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>eMedCareers</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.emedcareers.com/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                            <tr>
                                <td>Just Engineers</td>
                                <td>
                                    <CodeBlock CodeBlock>https://recruiter.justengineers.net/authorisationserver/token</CodeBlock>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <p>
                        To request an authorisation token you have to have a client_id and client_secret allocated to you as the
                        client. This is per brand, therefore you will get 5 separate sets of credentials.
                    </p>
                    <p>
                        The username and password are the customer&apos;s credentials for our services and are also specific to
                        the brand. As a customer can have access to multiple brands, it is important that searches are performed 
                        using a token obtained using the correct credentials for a given brand. It is important that you ensure 
                        that the customer&apos;s credentials are not shared across multiple accounts.
                    </p>
                    <p></p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>client_id</td>
                                <td>string</td>
                                <td>Your id provided by Stepstone UK.</td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>client_secret</td>
                                <td>string</td>
                                <td>Your secret provided by Stepstone UK.</td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>username</td>
                                <td>string</td>
                                <td>username (this could be an alias or email address) of the recruiter accessing the resource.</td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>password</td>
                                <td>string</td>
                                <td>password of the recruiter accessing the resource.</td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>grant_type</td>
                                <td>string</td>
                                <td>
                                    This will always be <b>password</b>.
                                </td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>scope</td>
                                <td>string</td>
                                <td>
                                    For this product this will always be <b>CVDatabase</b>.
                                </td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>client_user_id</td>
                                <td>string</td>
                                <td>
                                    Internal API client user identifier <i>(user id or username from your database)</i>.
                                </td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>client_device_id</td>
                                <td>string</td>
                                <td>
                                    Unique browser identifier{' '}
                                    <i>
                                        (long lifespan cookie with random unique value can be created and this value can be used as this
                                        browser device identifier.)
                                    </i>
                                </td>
                                <td>
                                    <i>Optional</i>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Card className="bg-dark text-white" body>
                        <h4>Curl Example</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`curl -X POST https://recruiter.totaljobs.com/AuthorisationServer/token \\\n-d 'username=example@stepstone.co.uk&password=Password1&grant_type=password&scope=CVDatabase&client_id=bbdb67a5-0e21-4aed-bec0-6cdb2b7e480d&client_secret=MySecret&client_user_id=ExampleId' \\\n-H 'content-type: application/x-www-form-urlencoded'`}
                        </CodeBlock>
                        <h4>REQUEST HEADERS</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            content-type: application/x-www-form-urlencoded
                        </CodeBlock>
                        <h4>RESPONSE HEADERS</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`content-type: application/json; charset=utf-8\nstatus: 200 OK`}
                        </CodeBlock>
                        <h4>RESPONSE BODY</h4>
                        <CodeBlock langauge="json" theme="dark">
                            {`{ \n\t"access_token": "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJyb2xlIjoiQ2FuZGlkYXRlIFNlYXJjaCBVc2VyIiwic2l0ZUlkIjoiNCIsInVuaXF1ZV9uYW1lIjoiMzM5MTc1YWQtMTEwNS00OWVkLTk3MzQtOTU0YzM5NzJjNTNjIiwiY2xpZW50SWQiOiIwMUQ0MzFCNy02REMwLTRERDEtQkYzMS0xRDQwNzlEMTI0QkEiLCJjbGllbnRVc2VySWQiOiJBIiwiaXNzIjoiUmVjcnVpdGVyIiwiYXVkIjoiYWxsIiwiZXhwIjoxNjE3MjcxODA3LCJuYmYiOjE2MTcyNzAwMDcsIlNzYVNlc3Npb25JZCI6ImNkODNjMTcyLThkZGUtNDEwNy1hNzRjLTY1NjlhMGU3YTQ2ZSJ9.gLBhEwpM5Ezh0PihC1ZCNdf4moZy2qNp1Y23NL_MYto",\n\t"token_type": "bearer",\n\t"expires_in": 1799\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="headervalue">Header Value</h3>
                    <p>
                        Please note that all API calls (except calls to Dictionary endpoint) require a valid token to be included
                        in the request header.
                    </p>
                    <p>
                        As a client of the service, you must request a separate token per customer of the API. It is important
                        that you retain the token for each customer using the service and are responsible for requesting
                        replacement tokens when they expire. The token must not be stored on the customer&apos;s machine
                        (browser session).
                    </p>
                    <Card className="bg-dark text-white" body>
                        <h4>REQUEST HEADERS</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`Authorization: bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJyb2xlIjoiQ2FuZGlkYXRlIFNlYXJjaCBVc2VyIiwic2l0ZUlkIjoiNCIsInVuaXF1ZV9uYW1lIjoiMzM5MTc1YWQtMTEwNS00OWVkLTk3MzQtOTU0YzM5NzJjNTNjIiwiY2xpZW50SWQiOiIwMUQ0MzFCNy02REMwLTRERDEtQkYzMS0xRDQwNzlEMTI0QkEiLCJjbGllbnRVc2VySWQiOiJBIiwiaXNzIjoiUmVjcnVpdGVyIiwiYXVkIjoiYWxsIiwiZXhwIjoxNjE3MjcxODA3LCJuYmYiOjE2MTcyNzAwMDcsIlNzYVNlc3Npb25JZCI6ImNkODNjMTcyLThkZGUtNDEwNy1hNzRjLTY1NjlhMGU3YTQ2ZSJ9.gLBhEwpM5Ezh0PihC1ZCNdf4moZy2qNp1Y23NL_MYto`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
        </section>
    )
}

export default AuthenticationSection
